import React, { useEffect } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { Form, Input } from "reactstrap"
import {
    digitealSelectors,
    FieldsetFormGroup,
    MixedMerchantDetails,
    SharedStoreState,
    ShowErrorMessages,
    TextButton,
    useSelector,
    valFuncRequired,
} from "swiipe.portal.shared"
import {
    addDigitealTechnicalUserThunk,
    fetchDigitealTechnicalUserThunk,
    IDigitealAddTechnicalUserData,
    removeDigitealTechnicalUserThunk,
} from "../../store/thunks/digitealThunks"
import useReduxDispatch from "../../store/useReduxDispatch"
import SubmitButton from "../buttons/SubmitButton"
import DigitealDetailsSection from "./DigitealDetailsSection"
import "./DigitealDetailsSectionTechnicalUser.scss"

interface IFormModel {
    technicalUserUsername: string
    technicalUserPassword: string
}

interface IDigitealDetailsSectionTechnicalUser {
    merchantDetails: MixedMerchantDetails
}

const DigitealDetailsSectionTechnicalUser = ({ merchantDetails }: IDigitealDetailsSectionTechnicalUser) => {
    const dispatch = useReduxDispatch()
    const { handleSubmit, register, setValue, errors, formState } = useForm()

    const technicalUser = useSelector((state: SharedStoreState) =>
        digitealSelectors.technicalUser(state, merchantDetails?.swMerchant.swMerchantId ?? "")
    )

    useEffect(() => {
        dispatch(fetchDigitealTechnicalUserThunk(merchantDetails.swMerchant.swMerchantId, false))
    }, [merchantDetails])

    const isUsingTechnicalUser = !!technicalUser?.username

    const onSubmit: SubmitHandler<IFormModel> = async (data: IFormModel) => {
        try {
            const formData: IDigitealAddTechnicalUserData = {
                swMerchantId: merchantDetails.swMerchant.swMerchantId,
                username: data.technicalUserUsername,
                password: data.technicalUserPassword,
            }
            await dispatch(addDigitealTechnicalUserThunk(formData))
        } catch {
            //Catch to stop showing spinner
        }
    }

    return (
        <DigitealDetailsSection
            title="Authentication source"
            description="Decide whether the integration authenticates through Aera or through a dedicated technical user."
            isLoading={technicalUser === undefined}
            sectionClassName="digiteal-technical-user"
        >
            <div className="digiteal-technical-user-status">
                <span className="digiteal-technical-user-status__indicator" />
                <span className="digiteal-technical-user-status__title">
                    {isUsingTechnicalUser ? "Using technical user" : "Aera is integrator"}
                </span>
            </div>
            {isUsingTechnicalUser && (
                <div className="digiteal-technical-active-user">
                    <div className="digiteal-technical-active-user__details">
                        <div className="icon" />
                        <div>
                            <strong className="digiteal-technical-active-user__username">{technicalUser.username}</strong>
                            <p className="digiteal-technical-active-user__description text-btn--font-size-smaller">
                                Active technical user for this integration flow.
                            </p>
                        </div>
                    </div>

                    <TextButton
                        text="Remove"
                        fontWeight="600"
                        className="digiteal-technical-user__button digiteal-technical-user__button--danger"
                        onClick={async () => {
                            try {
                                await dispatch(removeDigitealTechnicalUserThunk(merchantDetails.swMerchant.swMerchantId))
                            } catch {
                                //Catch to stop showing spinner
                            }
                        }}
                    />
                </div>
            )}
            {!isUsingTechnicalUser && (
                <Form onSubmit={handleSubmit(onSubmit)} className="digiteal-technical-add-user">
                    <h6 className="digiteal-technical-add-user__title">Add Technical User</h6>
                    <div>
                        <FieldsetFormGroup field>
                            <span className="digiteal-technical-add-user__label text-btn--font-size-smaller">Username</span>
                            <Input
                                type="text"
                                name="technicalUserUsername"
                                innerRef={register(valFuncRequired("username required"))}
                            />
                        </FieldsetFormGroup>
                        <FieldsetFormGroup field>
                            <span className="digiteal-technical-add-user__label text-btn--font-size-smaller">Password</span>
                            <Input
                                type="password"
                                name="technicalUserPassword"
                                innerRef={register(valFuncRequired("password required"))}
                            />
                        </FieldsetFormGroup>
                    </div>

                    <div className="digiteal-technical-add-user__footer">
                        <p className="digiteal-technical-add-user__hint">
                            A Technical user is only required when someone else is the integrator for the Digiteal account (e.g.
                            Terms.Tech)
                        </p>

                        <SubmitButton
                            dark
                            noBorder
                            noBorderRadius
                            containerClass="submit-btn"
                            formState={formState}
                            type="submit"
                            className="digiteal-technical-user__button"
                        >
                            Add
                        </SubmitButton>
                    </div>
                    <ShowErrorMessages formState={formState} />
                </Form>
            )}
        </DigitealDetailsSection>
    )
}

export default DigitealDetailsSectionTechnicalUser
